import type { MatchEvent } from "@arcade-hockey/shared";
import { tuning } from "../state/tuning.js";
import type { GoalieState, PuckState } from "../state/types.js";

export const GOALIE_SAVE_RADIUS = tuning.goalie.bodyRadius;

/**
 * Goalie body collision for the puck. Skips Recovering and pulled goalies.
 *
 * Behaviour by puck mode:
 *   - Shot:   produce a save event and rebound the puck back to the slot.
 *   - Pass / Loose: treat the goalie body as a solid circle — reflect the
 *     puck's velocity along the body normal so passes don't pass through
 *     the goalie and loose pucks don't trickle through the crease.
 *
 * Appending the save event directly into `events` (typically `state.events`)
 * avoids a per-tick array allocation in the hot path.
 */
export function resolveGoalieSaves(
  goalies: GoalieState[],
  puck: PuckState,
  currentTick: number,
  events: MatchEvent[],
): void {
  // Skip Controlled — the carrier owns the puck position (hard-pinned to
  // their stick anchor). Pushing the puck off the stick because a carrier
  // skated into the goalie crease would break the stick-anchor invariant.
  if (puck.mode === "Controlled") return;
  for (const goalie of goalies) {
    if (goalie.mode === "Recovering") continue;
    if (goalie.pulled) continue;

    const dx = puck.pos.x - goalie.pos.x;
    const dy = puck.pos.y - goalie.pos.y;
    const dist = Math.hypot(dx, dy);
    const contactDist = GOALIE_SAVE_RADIUS + tuning.puck.radius;
    if (dist > contactDist) continue;

    if (puck.mode === "Shot") {
      // Save: ramp goalie into Saving and rebound the puck back to the slot.
      goalie.mode = "Saving";
      goalie.modeTimer = tuning.goalie.reactionTicks;

      const sign = goalie.teamId === "home" ? 1 : -1;
      const speed = tuning.goalie.rebound.padReboundSpeed;
      puck.vel.x = sign * speed * 0.8;
      puck.vel.y = dy === 0 ? 0 : Math.sign(dy) * speed * 0.4;
      puck.mode = "Loose";
      puck.ownerId = null;
      puck.lastTouchedBy = goalie.id;
      puck.noRecaptureUntilTick = currentTick + tuning.puck.noRecaptureTicks;

      events.push({
        type: "GoalieSave",
        tick: currentTick,
        goalieId: goalie.id,
        saveType: "Pad",
      });
      break; // only one save per tick
    }

    // Non-shot collision: push the puck out of the goalie body along the
    // contact normal and reflect the inward velocity component. This
    // makes passes and loose pucks bounce off the goalie body the same
    // way they bounce off the boards.
    if (dist < 1e-6) continue; // coincident — degenerate; let next tick resolve
    const nx = dx / dist;
    const ny = dy / dist;
    const penetration = contactDist - dist;
    puck.pos.x += nx * penetration;
    puck.pos.y += ny * penetration;
    const inward = puck.vel.x * nx + puck.vel.y * ny;
    if (inward < 0) {
      // Reflect with low restitution — goalie pad absorbs most of the
      // impact, unlike a board bounce.
      const restitution = 0.4;
      puck.vel.x -= nx * inward * (1 + restitution);
      puck.vel.y -= ny * inward * (1 + restitution);
    }
    // Mark last-touched-by so the no-recapture window stops the carrier
    // immediately reclaiming a deflected pass on the goalie.
    puck.lastTouchedBy = goalie.id;
    break;
  }
}
